// one-decimal.directive.ts
import { Directive, HostListener, Input } from '@angular/core';

@Directive({
    selector: '[halfStep]'
})
export class HalfStepDirective {
    private stepSize = 0.5;

    @HostListener('input', ['$event'])
    onInput(event: Event): void {
        const inputEl = event.target as HTMLInputElement | HTMLTextAreaElement;
        const value = inputEl.value;

        const sanitizedValue = this.sanitize(value);
        if (sanitizedValue === value) return;

        inputEl.value = sanitizedValue;
    }

    // When user leaves the field -> round to next matching step
    @HostListener('blur', ['$event'])
    onBlur(event: FocusEvent): void {
        const inputEl = event.target as HTMLInputElement | HTMLTextAreaElement;
        const value = inputEl.value;

        if (!value || value === '.') return;

        const num = parseFloat(value);
        if (isNaN(num) || !this.stepSize || this.stepSize <= 0) return;

        const rounded = this.roundToStep(num, this.stepSize);
        const roundedStr = this.formatOneDecimal(rounded);

        inputEl.value = roundedStr;
    }

    /**
     * Keep only digits + one dot, max 1 decimal place.
     */
    private sanitize(value: string): string {
        let v = value.replace(/[^0-9.]/g, '');
        const firstDot = v.indexOf('.');

        if (firstDot !== -1) {
            const before = v.slice(0, firstDot + 1);
            const after = v.slice(firstDot + 1).replace(/\./g, '');
            v = before + after;

            const [intPart, decPart = ''] = v.split('.');
            v = intPart + '.' + decPart.slice(0, 1); // max 1 decimal
        }

        if (v.startsWith('.')) v = '0' + v;
        return v;
    }

    /**
     * Round a number to the nearest multiple of stepSize.
     * Example: num=0.3, step=0.25 → 0.25; num=0.38, step=0.25 → 0.5
     */
    private roundToStep(num: number, stepSize: number): number {
        const factor = 1 / stepSize;
        // Multiply → round → divide to avoid floating point noise
        return Math.round(num * factor) / factor;
    }

    /**
     * Convert to string with at most one decimal.
     */
    private formatOneDecimal(num: number): string {
        return num.toFixed(1).replace(/\.0$/, ''); // 1.0 -> "1"
    }
}